import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ActionSheetIOS, Platform, Pressable, View, StyleSheet } from 'react-native';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import { useModalStore, useChatInputStore, useToastStore } from '@/store';
import useFileParamUpload from '@/hooks/useFileParamUpload';
import useRequireAuth from '@/hooks/useRequireAuth';
import useTranslation from '@/hooks/useTranslation';
import {
  describeFileLimitViolation,
  formatFileUploadLimits,
  getFileValidationLimits,
} from '@/constants/utils/file_upload_limits';

const styles = StyleSheet.create({
  hiddenInput: {
    display: 'none',
  },
  dropdownContent: {
    minWidth: 220,
  },
  nativePressable: {
    // Keep the trigger inline-block-ish on native; FileParamGhostCard's
    // child View already sizes itself, we just need a pressable wrapper.
    alignSelf: 'flex-start',
  },
});

/**
 * Source-picker affordance for a file-typed custom parameter. Renders
 * platform-appropriate UI for choosing where the file comes from:
 *
 *   • Web (and desktop popovers) → A two-option dropdown menu offering
 *     "Select from library" (FilesModal) and "Upload file" (hidden input).
 *
 *   • iOS → Tapping the trigger opens a native ActionSheet matching the
 *     rest of the composer's attachment flow (Take Photo / Choose from
 *     Photos / Browse Files / Select from library). Camera + photo
 *     library options only appear when the param accepts image or video
 *     files.
 *
 *   • Android → Tapping the trigger opens a sheet-style dropdown menu
 *     with the same options.
 *
 * Accepts a `children` trigger which is rendered inside the platform's
 * pressable wrapper. Optional `onAddFile` / `onAddUploadingFile`
 * callbacks override the default destination (the current chat input
 * attachments).
 */
function FileSourceDropdown({
  param,
  children,
  onAddFile,
  onAddUploadingFile,
  align = 'start',
  sideOffset = 4,
  onOpenChange,
}) {
  const { t } = useTranslation();
  const { openModal } = useModalStore((state) => state.actions);
  const { addToast } = useToastStore((state) => state.actions);
  const addFileToChatInput = useChatInputStore((state) => state.addFile);
  const requireAuth = useRequireAuth();
  const [open, setOpen] = useState(false);
  const fileInputRef = useRef(null);
  const limitsHint = formatFileUploadLimits(getFileValidationLimits(param?.validation?.file));

  // Adding files (library or upload) is account-bound, so anonymous visitors
  // are routed through the shared auth CTA instead of the picker/library.
  const gateFileAction = useCallback(
    (action) => requireAuth(action, {
      source: 'tool_file_param',
      title: t('chatInput.authCta.uploadTitle') || 'Sign in to add files',
      subtitle: t('chatInput.authCta.uploadSubtitle') || 'Files are saved to your account so you can reuse them across chats.',
      icon: 'cloud-arrow-up',
    }),
    [requireAuth, t],
  );

  const {
    isUploading,
    acceptString,
    nativePickSources,
    pickAndUploadNative,
    handleWebFileInputChange,
  } = useFileParamUpload({ param, onAddFile, onAddUploadingFile });

  const handleOpenChange = useCallback(
    (next) => {
      setOpen(next);
      if (onOpenChange) onOpenChange(next);
    },
    [onOpenChange]
  );

  const handleSelectFromLibrary = useCallback(() => {
    setOpen(false);
    if (gateFileAction(() => true) !== true) return;
    const fileType = param?.validation?.file?.fileType || null;
    const title = param?.ui?.displayName
      ? `${t('chatInput.files.selectLabel') || 'Select'} ${param.ui.displayName}`
      : undefined;
    openModal('FilesModal', {
      fileType: fileType && fileType !== 'any' ? fileType : null,
      title,
      onSelect: (filesArg) => {
        const list = Array.isArray(filesArg) ? filesArg : (filesArg ? [filesArg] : []);
        if (!list.length) return;
        const limits = getFileValidationLimits(param?.validation?.file);
        // Library files are already uploaded — push them straight in.
        for (const f of list) {
          const limitError = describeFileLimitViolation(f, limits);
          if (limitError) {
            addToast(limitError, 'error');
            continue;
          }
          if (onAddFile) onAddFile(f);
          else addFileToChatInput(f);
        }
      },
    });
  }, [addFileToChatInput, addToast, onAddFile, openModal, param, t, gateFileAction]);

  const handleUploadFile = useCallback(async () => {
    setOpen(false);
    if (gateFileAction(() => true) !== true) return;
    if (Platform.OS === 'web') {
      fileInputRef.current?.click();
    } else {
      await pickAndUploadNative();
    }
  }, [pickAndUploadNative, gateFileAction]);

  // ---- iOS: action sheet matching the rest of the composer ----
  const handlePressIOS = useCallback(() => {
    if (isUploading) return;
    if (gateFileAction(() => true) !== true) return;
    const cancelLabel = t('chatInput.files.actionSheetCancel') || 'Cancel';
    const libraryLabel = t('chatInput.files.selectFromLibrary') || 'Select from library';
    const options = [cancelLabel];
    const runners = [null];
    for (const src of nativePickSources) {
      options.push(src.label);
      runners.push(src.run);
    }
    options.push(libraryLabel);
    runners.push(handleSelectFromLibrary);
    const paramCopy = param?.ui?.description || param?.description;
    const message = [paramCopy, limitsHint].filter(Boolean).join('\n') || undefined;
    ActionSheetIOS.showActionSheetWithOptions(
      {
        options,
        cancelButtonIndex: 0,
        title: param?.ui?.displayName || undefined,
        message,
      },
      (buttonIndex) => {
        const run = runners[buttonIndex];
        if (typeof run === 'function') {
          run();
        }
      }
    );
  }, [handleSelectFromLibrary, isUploading, limitsHint, nativePickSources, param, t, gateFileAction]);

  // ---- Native (non-iOS): wrap the trigger in a Zeego menu with the
  // expanded option set so Android / macOS users get the same fan-out. ----

  // ---- Web: keep the existing dropdown UI unchanged ----
  if (Platform.OS === 'web') {
    return (
      <View>
        <DropdownMenu.Root open={open} onOpenChange={handleOpenChange}>
          <DropdownMenu.Trigger>{children}</DropdownMenu.Trigger>
          <DropdownMenu.Content
            className="DropdownMenuContent"
            align={align}
            sideOffset={sideOffset}
            style={styles.dropdownContent}
          >
            <DropdownMenuItem
              icon="folder-open"
              iosIcon="folder"
              title={t('chatInput.files.selectFromLibrary') || 'Select from library'}
              onSelect={handleSelectFromLibrary}
              isDisabled={isUploading}
            />
            <DropdownMenuItem
              icon="arrow-up-from-bracket"
              iosIcon="arrow.up.doc"
              title={isUploading
                ? (t('chatInput.uploading') || 'Uploading…')
                : (t('chatInput.files.uploadFile') || 'Upload file')}
              subtitle={limitsHint || undefined}
              onSelect={handleUploadFile}
              isDisabled={isUploading}
            />
          </DropdownMenu.Content>
        </DropdownMenu.Root>

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleWebFileInputChange}
          accept={acceptString}
          style={styles.hiddenInput}
        />
      </View>
    );
  }

  // iOS: bypass Zeego entirely. ActionSheetIOS is the platform's standard
  // attachment-source picker (Photos, Camera, Files, …) and matches the
  // existing flows in `ComposerToolsMenu` / `ChatInputCustomParameterButton`.
  if (Platform.OS === 'ios') {
    return (
      <Pressable
        onPress={handlePressIOS}
        disabled={isUploading}
        style={styles.nativePressable}
      >
        {children}
      </Pressable>
    );
  }

  // Other native (Android, macOS): use the Zeego menu, which renders as a
  // native popup menu on Android. We expand the option list so users get
  // the same Take Photo / Photos / Files / Library choices.
  return (
    <View>
      <DropdownMenu.Root open={open} onOpenChange={handleOpenChange}>
        <DropdownMenu.Trigger>{children}</DropdownMenu.Trigger>
        <DropdownMenu.Content
          align={align}
          sideOffset={sideOffset}
          style={styles.dropdownContent}
        >
          {/* Raw menu items, not <DropdownMenuItem />: the Android native menu
              only picks up children whose element type is DropdownMenu.Item. */}
          {nativePickSources.map((src) => (
            <DropdownMenu.Item
              key={`file-source-${src.id}`}
              disabled={isUploading}
              onSelect={() => {
                setOpen(false);
                if (gateFileAction(() => true) !== true) return;
                src.run();
              }}
            >
              <DropdownMenu.ItemIcon ios={{ name: src.iosIcon }} />
              <DropdownMenu.ItemTitle>{src.label}</DropdownMenu.ItemTitle>
            </DropdownMenu.Item>
          ))}
          <DropdownMenu.Item
            key="file-source-library"
            disabled={isUploading}
            onSelect={handleSelectFromLibrary}
          >
            <DropdownMenu.ItemIcon ios={{ name: 'folder' }} />
            <DropdownMenu.ItemTitle>
              {t('chatInput.files.selectFromLibrary') || 'Select from library'}
            </DropdownMenu.ItemTitle>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </View>
  );
}

export default FileSourceDropdown;
